import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit — the living-room fireplace: a plaster surround around a dark
// firebox, an oak mantel shelf, two logs and a small flame. The flame part
// is named so the looping "flicker" animation can breathe its scale about
// its base. Back face sits at -Z to stand against a wall.
const PLASTER = "#e8e4dc";
const CONCRETE = "#9a9a94";
const OAK = "#c8975a";
const BLACK = "#2e2e30";
const LOG = "#7a5a3a";
const FLAME = "#e8833a";

const W = 1.0;
const H = 1.2;
const DEPTH = 0.32;
// Firebox opening: 0.64 wide, 0.72 high, floored by the hearth slab.
const OPEN_W = 0.64;
const OPEN_H = 0.72;
const HEARTH_H = 0.08;
const FLAME_BASE_Y = HEARTH_H + 0.1;

export const fireplace = defineModel({
  id: "fireplace",
  batchId: "modern",
  name: "Fireplace",
  tags: ["modern", "interior", "structure", "fireplace", "living-room", "animated"],
  summary: "A modern plaster fireplace with an oak mantel and a flickering flame.",
  description:
    "The living-room fireplace of the Modern Living kit: a plaster surround " +
    "1.0 m wide and 1.2 m high around a dark firebox, an oak mantel shelf, " +
    "two logs and a small flame with a looping flicker animation. The back " +
    "face is flat at -Z to stand against a wall.",
  tiles: { w: 1, h: 1 },
  footprint: 0.9,
  parts(): Part[] {
    const sideW = (W - OPEN_W) / 2;
    return [
      // Hearth base slab.
      { geometry: new THREE.BoxGeometry(W + 0.1, HEARTH_H, DEPTH + 0.14), color: CONCRETE,
        position: [0, HEARTH_H / 2, 0.03] },
      // Surround: back panel, two side columns, lintel above the opening.
      { geometry: new THREE.BoxGeometry(W, H - HEARTH_H, 0.1), color: PLASTER,
        position: [0, HEARTH_H + (H - HEARTH_H) / 2, -DEPTH / 2 + 0.05] },
      { geometry: new THREE.BoxGeometry(sideW, H - HEARTH_H, DEPTH), color: PLASTER,
        position: [-(OPEN_W + sideW) / 2, HEARTH_H + (H - HEARTH_H) / 2, 0] },
      { geometry: new THREE.BoxGeometry(sideW, H - HEARTH_H, DEPTH), color: PLASTER,
        position: [(OPEN_W + sideW) / 2, HEARTH_H + (H - HEARTH_H) / 2, 0] },
      { geometry: new THREE.BoxGeometry(OPEN_W, H - HEARTH_H - OPEN_H, DEPTH), color: PLASTER,
        position: [0, HEARTH_H + OPEN_H + (H - HEARTH_H - OPEN_H) / 2, 0] },
      // Dark firebox interior lining the back of the opening.
      { geometry: new THREE.BoxGeometry(OPEN_W, OPEN_H, 0.06), color: BLACK,
        position: [0, HEARTH_H + OPEN_H / 2, -DEPTH / 2 + 0.08] },
      // Oak mantel shelf.
      { geometry: new THREE.BoxGeometry(W + 0.12, 0.06, DEPTH + 0.1), color: OAK,
        position: [0, H + 0.03, 0.02] },
      // Two logs lying across the firebox.
      { geometry: new THREE.CylinderGeometry(0.055, 0.055, 0.42, 8), color: LOG,
        position: [0, HEARTH_H + 0.055, 0.0], rotation: [0, 0, Math.PI / 2] },
      { geometry: new THREE.CylinderGeometry(0.05, 0.05, 0.38, 8), color: LOG,
        position: [0.04, HEARTH_H + 0.14, -0.02], rotation: [0, 0.3, Math.PI / 2] },
      // The flame, named so the flicker animation drives it.
      { geometry: new THREE.ConeGeometry(0.09, 0.3, 8), color: FLAME,
        position: [0, FLAME_BASE_Y + 0.15, 0.02], name: "flame" },
    ];
  },
  animations: [
    {
      id: "flicker",
      label: "Flicker",
      duration: 1.2,
      loop: true,
      tracks: [
        {
          target: "flame",
          pivot: [0, FLAME_BASE_Y, 0.02],
          scale: [
            { t: 0, value: [1, 0.9, 1] },
            { t: 0.35, value: [0.94, 1.15, 0.94], ease: "sine" },
            { t: 0.75, value: [1.05, 0.85, 1.05], ease: "sine" },
            { t: 1.2, value: [1, 0.9, 1], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
